import { useCallback } from 'react';
import type { GanttData } from '../types';

type ImportResult = { success: boolean; error?: string };

type Args = {
  data: GanttData;
  setData: (d: GanttData) => void;
};

type Return = {
  exportToJson: () => void;
  importFromJson: (json: string) => ImportResult;
};

const isValidGanttData = (v: unknown): v is GanttData => {
  if (typeof v !== 'object' || v === null) return false;
  const d = v as Record<string, unknown>;
  return Array.isArray(d.tasks) && Array.isArray(d.rows) && Array.isArray(d.dependencies);
};

export const useGanttImportExport = ({ data, setData }: Args): Return => {
  const exportToJson = useCallback(() => {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gantt-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  const importFromJson = useCallback((json: string): ImportResult => {
    try {
      const parsed: unknown = JSON.parse(json);
      if (!isValidGanttData(parsed)) {
        return { success: false, error: 'Invalid format: expected { tasks[], rows[], dependencies[] }' };
      }
      setData(parsed);
      return { success: true };
    } catch (e) {
      return { success: false, error: `JSON parse error: ${(e as Error).message}` };
    }
  }, [setData]);

  return { exportToJson, importFromJson };
};
